import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useDeletePaymentDetails } from '../../hooks/payment-details/use-delete-payment-details';
import { PaymentDetailsEntry } from '../../types/payment-details';

const I18N = 'pages.finance.paymentDetails.deleteModal';

interface DeletePaymentDetailsModalProps {
  entry: PaymentDetailsEntry | null;
  open: boolean;
  onClose: () => void;
  /** Total non-deleted entries — used to warn when deleting the last one. */
  totalCount: number;
}

/**
 * Confirmation modal for deleting a payment-details entry. Already-generated
 * invoices are unaffected — the backend snapshots the text at creation time.
 * Warns when the deleted entry is the current default, since deletion never
 * auto-promotes another entry to default.
 */
export const DeletePaymentDetailsModal = ({
  entry,
  open,
  onClose,
  totalCount,
}: DeletePaymentDetailsModalProps) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const deleteMutation = useDeletePaymentDetails();

  const isDeletingLastEntry = totalCount === 1;
  const isDeletingDefault = Boolean(entry?.isDefault);

  const handleDelete = async (closeModal: () => void) => {
    if (!entry) return;

    try {
      await deleteMutation.mutateAsync(entry.id);
      closeModal();
      onClose();
    } catch {
      // Error surfaced by the mutation's onError handler.
    }
  };

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      title={t(`authenticated.settings:${I18N}.title`)}
      message={t(`authenticated.settings:${I18N}.description`, { name: entry?.name ?? '' })}
      confirmText={t(`authenticated.settings:${I18N}.confirm`)}
      cancelText={t('common:cancel')}
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteMutation.isPending}
    >
      {isDeletingDefault && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t(
              isDeletingLastEntry
                ? `authenticated.settings:${I18N}.warningLastDefault`
                : `authenticated.settings:${I18N}.warningDefault`
            )}
          </AlertDescription>
        </Alert>
      )}
    </ConfirmationModal>
  );
};
